// src/pages/Login.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../firebase";
import {
  TextField,
  Button,
  Typography,
  Container,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";

import { getFirestore, doc, getDoc } from "firebase/firestore";


const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSending, setResetSending] = useState(false);

  const isStudentEmail = email.trim().toLowerCase().endsWith("@yourstoryworld.com");

  const routeByUserDoc = async (uid) => {
    const db = getFirestore();

    // 1) Student?
    const studentSnap = await getDoc(doc(db, "students", uid));
    if (studentSnap.exists()) {
      const s = studentSnap.data() || {};
      if (s.schoolId) localStorage.setItem("schoolId", s.schoolId);
      if (s.className) localStorage.setItem("className", s.className);
      navigate("/student-dashboard");
      return;
    }

    // 2) Teacher?
    const teacherSnap = await getDoc(doc(db, "teachers", uid));
    if (teacherSnap.exists()) {
      const t = teacherSnap.data() || {};
      if (t.schoolId) localStorage.setItem("schoolId", t.schoolId);
      if (t.className) localStorage.setItem("className", t.className);
      localStorage.setItem("teacherId", uid);
      navigate("/teacher-dashboard");
      return;
    }

    // 3) Regular user/org (Free/Personal/Family/School)
    const userSnap = await getDoc(doc(db, "users", uid));
    if (userSnap.exists()) {
      const u = userSnap.data() || {};
      const role = String(u.role || "").toLowerCase();
      const plan = String(u.plan || "").toLowerCase();

      // School org account (new or legacy naming)
      if (role === "school" || plan.startsWith("schools")) {
        localStorage.setItem("schoolId", u.schoolId || uid);
        navigate("/school-dashboard");
        return;
      }

      // Family plans (support legacy & new names)
      if (plan.includes("family")) {
        navigate("/family-dashboard");
        return;
      }

      // Personal
      if (plan.includes("personal")) {
        navigate("/personal-dashboard");
        return;
      }

      // Free / unknown -> free dashboard
      navigate("/free-dashboard");
      return;
    }

    // Fallback (no doc found anywhere)
    navigate("/dashboard");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setResetMsg("");
    setLoading(true);

    try {
      const cred = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password
      );
      await routeByUserDoc(cred.user.uid);
    } catch (err) {
      console.error("❌ Login Error:", err);
      const map = {
        "auth/invalid-email": "That email address looks invalid.",
        "auth/user-disabled": "This account has been disabled.",
        "auth/user-not-found": "No account found with that email.",
        "auth/wrong-password": "Incorrect password.",
        "auth/too-many-requests":
          "Too many attempts. Please wait a bit and try again.",
      };
      setError(map[err.code] || "Invalid email or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
  setError("");
  setResetMsg("");

  const targetEmail = email.trim();
  if (!targetEmail) {
    setError(
      "Please enter your email above first, then click ‘Forgot Password?’.",
    );
    return;
  }

  // ⛔ Block resets for school-issued student logins
  if (targetEmail.toLowerCase().endsWith("@yourstoryworld.com")) {
    setError(
      "For student accounts created by your school, please ask your teacher or school to reset your password."
    );
    return;
  }

  try {
    setResetSending(true);
    await sendPasswordResetEmail(auth, targetEmail);
    setResetMsg(
      `Password reset email sent to ${targetEmail}. Check your inbox (and spam).`,
    );
  } catch (err) {
    console.error("Reset error:", err);
    const map = {
      "auth/invalid-email": "That email address looks invalid.",
      "auth/user-not-found": "No account found with that email.",
      "auth/too-many-requests":
        "Too many requests. Please wait a bit and try again.",
    };
    setError(map[err.code] || "Could not send reset email. Please try again.");
  } finally {
    setResetSending(false);
  }
};


  return (
    <div
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "100vh",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: "50px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <div>
          <Button color="inherit" onClick={() => navigate("/")}>
            Home
          </Button>
          <Button color="inherit" onClick={() => navigate("/help")}>
            Help
          </Button>
        </div>
      </header>

      <Container maxWidth="sm" style={{ marginTop: "40px" }}>
        <Card
          style={{
            padding: "30px",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            borderRadius: "10px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            textAlign: "center",
          }}
        >
          <CardContent>
            <Typography
              variant="h4"
              style={{ fontWeight: "bold", marginBottom: "20px" }}
            >
              Sign In
            </Typography>

            {error && (
              <Typography sx={{ color: "error.main", mb: 1 }}>{error}</Typography>
            )}
            {resetMsg && (
              <Typography sx={{ color: "success.main", mb: 1 }}>
                {resetMsg}
              </Typography>
            )}

            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                margin="normal"
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                margin="normal"
              />

              <Tooltip
  title={
    isStudentEmail
      ? (
          <span>
            <strong>Student account:</strong> Your password can only be reset by your
            teacher or school. Please ask them to update it for you.
          </span>
        )
      : (
          <span>
            <strong>Parents (Plans) & teachers:</strong> We’ll email a secure reset link to your e-mail
            address. For <strong>student logins</strong>, password changes are handled by the school so please contact your teacher/school.
          </span>
        )
  }
  placement="right"
  arrow
  enterDelay={300}
  leaveDelay={100}
  slotProps={{
    tooltip: {
      sx: {
        bgcolor: "#1E3A8A",
        color: "#ffffff",
        fontSize: "0.78rem",
        maxWidth: 260,
        padding: "8px 10px",
        borderRadius: "8px",
        boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
      },
    },
    arrow: {
      sx: {
        color: "#1E3A8A",
      },
    },
  }}
>
  <Typography
    variant="body2"
    sx={{
      cursor: resetSending ? "not-allowed" : "pointer",
      mt: 1,
      opacity: resetSending ? 0.6 : 1,
      textDecoration: "underline",
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      color: isStudentEmail ? "#b45309" : "#1976d2", // amber-ish for students, default blue otherwise
      fontWeight: isStudentEmail ? 600 : 400,
    }}
    onClick={resetSending ? undefined : handleResetPassword}
  >
    {resetSending ? (
      "Sending reset email..."
    ) : (
      <>
        {isStudentEmail && (
          <LockResetIcon
            fontSize="small"
            sx={{ verticalAlign: "middle" }}
          />
        )}
        Forgot Password?
      </>
    )}
  </Typography>
</Tooltip>




              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                style={{ marginTop: "20px" }}
                disabled={loading}
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <Typography variant="body2" style={{ marginTop: "15px" }}>
              Don’t have an account?{" "}
              <span
                style={{ color: "#007BFF", cursor: "pointer" }}
                onClick={() => navigate("/subscriptions")}
              >
                Choose a plan to get started
              </span>
            </Typography>
          </CardContent>
        </Card>
      </Container>

      <footer
        className="page-footer"
        style={{ marginTop: "20px", textAlign: "center", padding: "15px 0" }}
      >
        <Typography>
          &copy; {new Date().getFullYear()} YourStoryWorld. All rights reserved.
        </Typography>
      </footer>
    </div>
  );
};

export default Login;
